<template>
  <v-container fluid class="pa-4 grey lighten-4">
    <!-- ============================================ -->
    <!-- VISTA PRINCIPAL: LISTA DE USUARIOS -->
    <!-- ============================================ -->
    <v-expand-transition>
      <div v-show="step == 1">
        <!-- KPIs Dashboard (compactos) -->
        <v-row class="mb-2" dense>
          <v-col cols="6" sm="3">
            <v-card class="elevation-2 rounded-lg" color="primary" dark>
              <v-card-text class="pa-2 d-flex align-center justify-space-between">
                <div>
                  <div class="text-h6 font-weight-bold">{{ users.length }}</div>
                  <div class="caption">Total Usuarios</div>
                </div>
                <v-icon size="30" class="opacity-50">mdi-account-group</v-icon>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="6" sm="3">
            <v-card class="elevation-2 rounded-lg" color="success" dark>
              <v-card-text class="pa-2 d-flex align-center justify-space-between">
                <div>
                  <div class="text-h6 font-weight-bold">{{ activeUsersCount }}</div>
                  <div class="caption">Usuarios Activos</div>
                </div>
                <v-icon size="30" class="opacity-50">mdi-account-check</v-icon>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="6" sm="3">
            <v-card class="elevation-2 rounded-lg" color="warning" dark>
              <v-card-text class="pa-2 d-flex align-center justify-space-between">
                <div>
                  <div class="text-h6 font-weight-bold">{{ inactiveUsersCount }}</div>
                  <div class="caption">Usuarios Inactivos</div>
                </div>
                <v-icon size="30" class="opacity-50">mdi-account-off</v-icon>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="6" sm="3">
            <v-card class="elevation-2 rounded-lg" color="info" dark>
              <v-card-text class="pa-2 d-flex align-center justify-space-between">
                <div>
                  <div class="text-h6 font-weight-bold">{{ modulesCount }}</div>
                  <div class="caption">Módulos Activos</div>
                </div>
                <v-icon size="30" class="opacity-50">mdi-view-module</v-icon>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Tabla Principal -->
        <v-card class="elevation-4 rounded-lg">
          <!-- Header + Filtros (fijos arriba) -->
          <div class="user-filters-sticky">
            <v-card-title class="primary white--text py-3 px-4">
              <v-icon class="mr-2" color="white">mdi-shield-account</v-icon>
              <span class="text-h6 font-weight-bold">Gestión de Usuarios</span>
              <v-spacer></v-spacer>
              <v-btn small color="white" class="primary--text font-weight-bold" elevation="2"
                @click="clearUser(), (step = 2), (editStep = 1)">
                <v-icon left small>mdi-account-plus</v-icon>
                Nuevo Usuario
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <div class="white px-4 py-2">
              <v-row dense align="center">
                <v-col cols="12" md="4">
                  <v-text-field v-model="search" prepend-inner-icon="mdi-magnify"
                    label="Buscar por nombre, usuario o código" outlined dense clearable hide-details></v-text-field>
                </v-col>
                <v-col cols="6" md="2">
                  <v-select v-model="module_filter" :items="computed_modules" item-value="id"
                    item-text="description" label="Módulo" outlined dense clearable hide-details
                    prepend-inner-icon="mdi-view-module"></v-select>
                </v-col>
                <v-col cols="6" md="2">
                  <v-select v-model="status_filter" :items="statusOptions" label="Estado" outlined dense clearable
                    hide-details prepend-inner-icon="mdi-checkbox-marked-circle"></v-select>
                </v-col>
                <v-col cols="12" md="2">
                  <v-autocomplete v-model="location_filter" :items="constAllLocations" item-value="id"
                    item-text="name" label="Planta" outlined dense clearable hide-details
                    prepend-inner-icon="mdi-map-marker"></v-autocomplete>
                </v-col>
                <v-col cols="12" md="2" class="d-flex align-center">
                  <v-switch v-model="custom_filter" dense hide-details class="mt-0 pt-0" color="warning"
                    label="Solo custom"></v-switch>
                  <v-btn v-if="activeFiltersCount > 0" icon small color="error" class="ml-1" @click="clearAllFilters"
                    title="Limpiar filtros">
                    <v-icon small>mdi-filter-remove</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </div>
            <v-divider></v-divider>
          </div>

          <v-card-text class="pa-2">

            <!-- Tabla de Usuarios -->
            <v-data-table :headers="headerUser" :items="filteredUsers" :search="search" :loading="loading"
              loading-text="Cargando usuarios..." class="elevation-1" :items-per-page="15" :footer-props="{
                'items-per-page-options': [10, 15, 25, 50, 100],
                'items-per-page-text': 'Usuarios por página:',
              }">
              <!-- Código de Empleado -->
              <template v-slot:[`item.employee_code`]="{ item }">
                <v-chip small color="grey lighten-3" class="font-weight-bold">
                  {{ item.employee_code || "N/A" }}
                </v-chip>
              </template>

              <!-- Nombre con Avatar -->
              <template v-slot:[`item.fullname`]="{ item }">
                <div class="d-flex align-center">
                  <v-avatar size="32" color="primary" class="mr-2">
                    <v-img v-if="item.image_storage" :src="get_storage(item.image_storage)"></v-img>
                    <span v-else class="white--text text-subtitle-2">
                      {{ getInitials(item.fullname) }}
                    </span>
                  </v-avatar>
                  <span class="font-weight-medium">{{ item.fullname }}</span>
                </div>
              </template>

              <!-- Username -->
              <template v-slot:[`item.username`]="{ item }">
                <div class="d-flex align-center">
                  <v-icon small color="grey" class="mr-1">mdi-at</v-icon>
                  <span>{{ item.username }}</span>
                </div>
              </template>

              <!-- Módulos con Chips -->
              <template v-slot:[`item.modules_access`]="{ item }">
                <div class="d-flex flex-wrap">
                  <v-chip v-for="(module, index) in item.modules_access.split('/')" :key="index" x-small
                    :color="getModuleColor(module)" dark class="ma-1">
                    {{ module }}
                  </v-chip>
                </div>
              </template>

              <!-- Plantas con Badge -->
              <template v-slot:[`item.locations_quantity`]="{ item }">
                <v-badge :content="item.locations_quantity" :color="item.locations_quantity > 0 ? 'success' : 'grey'"
                  inline>
                  <v-icon small>mdi-map-marker-multiple</v-icon>
                </v-badge>
              </template>

              <!-- Último Acceso -->
              <template v-slot:[`item.last_login`]="{ item }">
                <div class="d-flex align-center">
                  <v-icon small :color="isRecentLogin(item.last_login) ? 'success' : 'grey'" class="mr-1">
                    mdi-clock-outline
                  </v-icon>
                  <span class="text-caption">{{ item.last_login }}</span>
                </div>
              </template>

              <!-- Estado Mejorado -->
              <template v-slot:[`item.status_description`]="{ item }">
                <v-chip small :color="item.status ? 'success' : 'error'" dark class="font-weight-bold">
                  <v-icon small left>
                    {{ item.status ? "mdi-check-circle" : "mdi-close-circle" }}
                  </v-icon>
                  {{ item.status_description }}
                </v-chip>
              </template>

              <!-- Acciones Mejoradas -->
              <template v-slot:[`item.actions`]="{ item }">
                <v-menu offset-y>
                  <template v-slot:activator="{ attrs, on }">
                    <v-btn color="primary" small v-bind="attrs" v-on="on" class="font-weight-bold">
                      Acciones
                      <v-icon right small>mdi-menu-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="editUser(item, 'edit')">
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="primary">mdi-pencil</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Editar</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="editUser(item, 'copy')">
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="success">mdi-content-copy</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Copiar</v-list-item-title>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item @click="viewUserDetails(item)">
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="info">mdi-eye</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Ver Detalles</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <!-- Estadísticas por Módulo -->
        <v-row class="mt-4">
          <v-col cols="12">
            <v-card class="elevation-3 rounded-lg">
              <v-card-title class="grey lighten-3">
                <v-icon left color="primary">mdi-chart-bar</v-icon>
                <span class="font-weight-bold">Distribución por Módulo</span>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col v-for="module in system_modules" :key="module.id" cols="12" sm="6" md="3">
                    <v-card outlined class="text-center pa-3">
                      <v-icon large :color="getModuleColor(module.code)">
                        mdi-view-module
                      </v-icon>
                      <div class="text-h5 font-weight-bold mt-2">
                        {{ getUsersByModule(module.id) }}
                      </div>
                      <div class="text-subtitle-2 grey--text">
                        {{ module.description }}
                      </div>
                      <v-progress-linear :value="(getUsersByModule(module.id) / users.length) * 100"
                        :color="getModuleColor(module.code)" height="6" rounded class="mt-2"></v-progress-linear>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-expand-transition>

    <!-- ============================================ -->
    <!-- VISTA SECUNDARIA: FORMULARIO DE USUARIO -->
    <!-- ============================================ -->
    <v-expand-transition>
      <v-card flat v-if="step == 2" class="elevation-4 rounded-lg">
        <!-- Header del Formulario (Wizard Beta) -->
        <v-card-title class="primary white--text pa-5">
          <v-btn icon dark @click="exitForm()" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-icon large class="mr-3">mdi-account-edit</v-icon>
          <span class="text-h5 font-weight-bold">
            {{ user.id == null ? "Nuevo Usuario" : "Editar a " + (user.fullname || "") }}
          </span>
          <span v-if="user.id && user.username" class="caption ml-2">@{{ user.username }}</span>
          <v-spacer></v-spacer>
          <v-btn small color="success" class="mr-2" @click="save">
            <v-icon left small>mdi-content-save</v-icon>{{ user.id == null ? "Guardar" : "Actualizar" }}
          </v-btn>
          <v-btn v-if="user.id" small color="white" class="primary--text mr-2" :loading="exporting"
            @click="downloadUserXLSX">
            <v-icon left small>mdi-file-excel</v-icon>Exportar
          </v-btn>
          <v-btn small outlined color="white" class="mr-2" @click="exitForm()">Cancelar</v-btn>
          <v-chip v-if="user.id" color="white" text-color="primary">ID: {{ user.id }}</v-chip>
        </v-card-title>
        <v-divider></v-divider>

        <v-stepper v-model="editStep" flat non-linear class="elevation-0">
          <v-stepper-header>
            <v-stepper-step :step="1" editable :complete="editStep > 1">Datos personales</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :step="2" editable :complete="editStep > 2">Módulos</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :step="3" editable :complete="editStep > 3">Perfiles y accesos</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :step="4" editable>Plantas ({{ locationAccess.length }})</v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <!-- PASO 1: Datos personales -->
            <v-stepper-content :step="1">
              <v-row>
                <v-col cols="12" md="3" class="d-flex flex-column align-center justify-center">
                  <v-avatar size="170" color="grey lighten-2" class="elevation-2">
                    <v-img contain :src="profileImageDetail || require('@/assets/user.png')">
                      <template v-slot:placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                          <v-progress-circular indeterminate color="primary"></v-progress-circular>
                        </v-row>
                      </template>
                    </v-img>
                  </v-avatar>
                  <div class="text-h6 font-weight-bold mt-3 text-center">{{ user.fullname || "Nombre del Usuario" }}</div>
                  <div class="text-caption grey--text">{{ user.username ? "@" + user.username : "@username" }}</div>
                </v-col>
                <v-col cols="12" md="9">
                    <v-card class="mb-4 elevation-2">
                      <v-card-title class=" py-3">
                        <v-icon left color="primary">mdi-account-details</v-icon>
                        <span class="text-h6 font-weight-bold">Información Personal</span>
                      </v-card-title>
                      <v-divider></v-divider>
                      <v-card-text class="pa-4">
                        <!-- Fila 1: Código / Nombre -->
                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field :rules="codeRule" v-model="user.employee_code" label="Código de Empleado"
                              outlined dense append-icon="mdi-magnify" @click:append="validate_code()"
                              @keyup.enter="validate_code()" maxlength="6" :loading="loadSearch"
                              hint="Presione Enter o haga clic en la lupa" persistent-hint></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field v-model="user.fullname" label="Nombre Completo" outlined dense
                              prepend-inner-icon="mdi-account"></v-text-field>
                          </v-col>
                        </v-row>
                        <!-- Fila 2: Usuario / Correo -->
                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field v-model="user.username" @change="validateAvailability"
                              label="Nombre de Usuario" outlined dense prepend-inner-icon="mdi-at"
                              hint="Mínimo 5 caracteres" persistent-hint></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field v-model="user.email" label="Correo Electrónico" dense outlined
                              :rules="emailRule" prepend-inner-icon="mdi-email" type="email"></v-text-field>
                          </v-col>
                        </v-row>
                        <!-- Fila 3: Teléfono / Estado -->
                        <v-row align="center">
                          <v-col cols="12" md="6">
                            <v-text-field v-model="user.use_telephone" label="Teléfono" dense outlined
                              prepend-inner-icon="mdi-phone"></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-card outlined class="pa-3 text-center">
                              <div class="text-caption grey--text mb-2">
                                Estado del Usuario
                              </div>
                              <v-switch v-model="user.status" @change="
                                (dialogChangeStatus = true), (is_delete = false)
                                " color="success" inset hide-details class="mt-0 d-flex justify-center">
                                <template v-slot:label>
                                  <span class="font-weight-bold" :class="user.status ? 'success--text' : 'error--text'">
                                    {{ user.status ? "Activo" : "Inactivo" }}
                                  </span>
                                </template>
                              </v-switch>
                            </v-card>
                          </v-col>
                        </v-row>
                        <!-- Fila 4: Puesto / Departamento (solo lectura, de RRHH) -->
                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field :value="user.jobtitle || 'No disponible'" label="Puesto" dense outlined
                              readonly filled prepend-inner-icon="mdi-badge-account-horizontal"></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field :value="user.department || 'No disponible'" label="Departamento" dense outlined
                              readonly filled prepend-inner-icon="mdi-office-building"></v-text-field>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                </v-col>
              </v-row>
              <div class="d-flex justify-space-between mt-3">
                <v-btn text color="warning" :disabled="!user.id" @click="dialogConfirm = true">
                  <v-icon left small>mdi-lock-reset</v-icon>Reiniciar contraseña
                </v-btn>
                <v-btn color="primary" @click="editStep = 2">Siguiente<v-icon right>mdi-arrow-right</v-icon></v-btn>
              </div>
            </v-stepper-content>

            <!-- PASO 2: Módulos -->
            <v-stepper-content :step="2">
                  <v-card class="mb-4 elevation-2">
                    <v-card-title class="primary white--text py-3">
                      <v-icon left color="white" small>mdi-view-module</v-icon>
                      <span class="text-subtitle-1 font-weight-bold">Módulos</span>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="pa-2">
                      <v-list dense class="pa-0">
                        <template v-for="module in system_modules">
                          <v-list-item :key="module.id" class="px-0">
                            <v-list-item-content class="py-1">
                              <div class="d-flex align-center justify-space-between">
                                <v-switch dense v-model="user[module.module_key]" :label="module.description"
                                  @change="add_or_remove_module_user(module)" color="primary" hide-details
                                  class="mt-0"></v-switch>
                                <v-switch dense label="Custom" :disabled="!user[module.module_key]"
                                  v-model="user[module.module_custom_key]" @change="update_custom_etting(module)"
                                  color="warning" hide-details class="mt-0 ml-2"></v-switch>
                              </div>
                            </v-list-item-content>
                          </v-list-item>
                          <v-divider :key="`div-${module.id}`"></v-divider>
                        </template>
                      </v-list>
                    </v-card-text>
                  </v-card>

                <div class="d-flex justify-space-between mt-3">
                  <v-btn text @click="editStep = 1"><v-icon left>mdi-arrow-left</v-icon>Anterior</v-btn>
                  <v-btn color="primary" @click="editStep = 3">Siguiente<v-icon right>mdi-arrow-right</v-icon></v-btn>
                </div>
            </v-stepper-content>

            <!-- PASO 3: Perfiles y accesos -->
            <v-stepper-content :step="3">
                    <v-row no-gutters>
                      <!-- Permisos del módulo seleccionado (derecha) -->
                      <v-col cols="12" md="9" order-md="2" class="pl-md-3">
                        <template v-if="selectedModule">
                          <div class="d-flex align-center flex-wrap mb-2">
                            <span class="text-caption font-weight-bold grey--text mr-3">
                              <v-icon left x-small>mdi-account-key</v-icon>Perfil de {{ selectedModule.description }}:
                            </span>
                            <v-chip v-for="item in system_profiles.filter(
                              (element) => element.module_id == selectedModule.id
                            )" :key="item.id" :color="get_color(item)"
                              :outlined="get_color(item) === 'primary'" label small
                              @click="add_user_profile(item, selectedModule)" :disabled="!user[selectedModule.module_key]"
                              class="ma-1">
                              {{ item.description }}
                              <v-icon v-show="get_color(item) == 'success'" right x-small>mdi-check-circle</v-icon>
                            </v-chip>
                          </div>

                          <v-card flat outlined>
                            <v-card-subtitle class="font-weight-bold grey--text py-2 d-flex align-center">
                              <v-icon left small>mdi-shield-check</v-icon>
                              Permisos Específicos — {{ selectedModule.description }}
                              <v-chip x-small color="primary" class="ml-2" v-if="check_cant_edit(selectedModule)">Modo Perfil</v-chip>
                              <v-chip x-small color="warning" class="ml-2" v-else>Modo Custom</v-chip>
                              <v-chip x-small color="primary" outlined class="ml-2">
                                {{ selectedModuleCounts.active }} / {{ selectedModuleCounts.total }} activos
                              </v-chip>
                              <v-chip x-small color="error" class="ml-2" v-if="extraFeaturesCount > 0">
                                <v-icon left x-small>mdi-alert-circle</v-icon>
                                {{ extraFeaturesCount }} fuera del perfil
                              </v-chip>
                              <v-spacer></v-spacer>
                              <v-btn x-small text color="primary" @click="toggleAllGroups">
                                <v-icon left x-small>{{ allGroupsCollapsed ? "mdi-unfold-more-horizontal" : "mdi-unfold-less-horizontal" }}</v-icon>
                                {{ allGroupsCollapsed ? "Expandir todo" : "Colapsar todo" }}
                              </v-btn>
                            </v-card-subtitle>
                            <v-divider></v-divider>
                            <v-data-table ref="permTable" :headers="headers_feature"
                              :items="get_system_features_by_module(selectedModule.id)" item-key="feature_code"
                              sort-by="view_code" group-by="view_description" show-group-by hide-default-footer
                              dense height="52vh" :items-per-page="-1" class="striped-table">
                              <template v-slot:group.header="{ items, isOpen, toggle }">
                                <th colspan="3">
                                  <v-btn @click="toggle" small icon class="mr-2">
                                    <v-icon>{{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                                  </v-btn>
                                  <v-icon left small color="primary">mdi-view-dashboard</v-icon>
                                  <span class="font-weight-bold">{{ items[0].view_description }}</span>
                                  <v-chip x-small class="ml-2"
                                    :color="items.filter(i => i.include).length ? 'primary' : 'grey lighten-2'"
                                    :text-color="items.filter(i => i.include).length ? 'white' : 'grey darken-1'">
                                    {{ items.filter(i => i.include).length }} / {{ items.length }}
                                  </v-chip>
                                </th>
                              </template>
                              <template v-slot:[`item.description`]="{ item }">
                                <div class="d-flex align-center">
                                  <v-tooltip top v-if="isExtraFeature(item)">
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon small color="error" class="mr-2" v-bind="attrs" v-on="on">mdi-alert-circle</v-icon>
                                    </template>
                                    <span>Acceso adicional: no pertenece al perfil asignado</span>
                                  </v-tooltip>
                                  <v-icon v-else small color="grey" class="mr-2">mdi-shield-outline</v-icon>
                                  <span :class="{ 'error--text font-weight-medium': isExtraFeature(item) }">{{ item.description }}</span>
                                </div>
                              </template>
                              <template v-slot:[`item.include`]="{ item }">
                                <v-checkbox dense :disabled="check_cant_edit(selectedModule)" hide-details
                                  v-model="item.include" color="success" class="mt-0">
                                  <template v-slot:label>
                                    <span class="text-caption" :class="item.include ? 'success--text font-weight-bold' : 'grey--text'">
                                      {{ item.include ? "Permitido" : "No permitido" }}
                                    </span>
                                  </template>
                                </v-checkbox>
                              </template>
                            </v-data-table>
                          </v-card>
                        </template>
                        <div v-else class="pa-8 text-center grey--text">
                          <v-icon size="48" color="grey lighten-1">mdi-gesture-tap</v-icon>
                          <div class="mt-2">Selecciona un módulo de la lista para ver sus perfiles y permisos.</div>
                        </div>
                      </v-col>

                      <!-- Lista de módulos (izquierda) -->
                      <v-col cols="12" md="3" order-md="1">
                        <v-card outlined>
                          <v-subheader class="font-weight-bold">MÓDULOS</v-subheader>
                          <v-divider></v-divider>
                          <v-list dense class="py-0" style="max-height: 58vh; overflow-y: auto;">
                            <v-list-item-group v-model="module_tab" color="primary">
                              <v-list-item v-for="(module, i) in system_modules" :key="module.id" :value="i"
                                :disabled="!user[module.module_key]">
                                <v-list-item-icon class="mr-2"><v-icon small>mdi-view-module</v-icon></v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title>{{ module.description }}</v-list-item-title>
                                </v-list-item-content>
                                <v-icon v-if="user[module.module_custom_key]" small color="warning">mdi-cog</v-icon>
                              </v-list-item>
                            </v-list-item-group>
                          </v-list>
                        </v-card>
                      </v-col>
                    </v-row>

                <div class="d-flex justify-space-between mt-3">
                  <v-btn text @click="editStep = 2"><v-icon left>mdi-arrow-left</v-icon>Anterior</v-btn>
                  <v-btn color="primary" @click="editStep = 4">Siguiente<v-icon right>mdi-arrow-right</v-icon></v-btn>
                </div>
            </v-stepper-content>

            <!-- PASO 4: Plantas -->
            <v-stepper-content :step="4">
                    <v-card class="mb-4 elevation-2">
                      <v-card-title class="teal white--text py-3">
                        <v-icon left color="white">mdi-map-marker-multiple</v-icon>
                        <span class="text-h6 font-weight-bold">Plantas con Acceso</span>
                        <v-spacer></v-spacer>
                        <v-chip color="white" text-color="teal" small>
                          {{ locationAccess.length }} seleccionadas
                        </v-chip>
                      </v-card-title>
                      <v-divider></v-divider>
                      <v-card-text class="pa-4">
                        <DataIterator :all_items="constAllLocations" :items_selected="locationAccess"
                          :hide_footer="true" vh_height="40vh" :title="{
                            all: 'Plantas Disponibles',
                            selected: 'Plantas con Acceso',
                          }" />
                      </v-card-text>
                    </v-card>

                <div class="d-flex justify-space-between mt-3">
                  <v-btn text @click="editStep = 3"><v-icon left>mdi-arrow-left</v-icon>Anterior</v-btn>
                  <v-btn color="success" @click="save"><v-icon left>mdi-content-save</v-icon>{{ user.id == null ? "Guardar" : "Actualizar" }}</v-btn>
                </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card>
    </v-expand-transition>

    <!-- Diálogos de Confirmación -->
    <DialogConfirm :showDialog="dialogConfirm" @closeDialog="resetPassword" :confirm_settings="{
      icon: 'mdi-lock-reset', size: '45%', title: 'Reiniciar contraseña',
      sub_title: '¿Está seguro que desea reiniciar la contraseña de este usuario?',
      btn_affirmative: 'Sí, reiniciar', btn_negative: 'Cancelar'
    }" />
    <DialogConfirm :showDialog="dialogChangeStatus" @closeDialog="changeUserStatus" :confirm_settings="{
      icon: 'mdi-account-alert', size: '45%', title: 'Cambiar estado del usuario',
      sub_title: '¿Está seguro que desea ' + getStatusUser.title + ' este usuario?',
      btn_affirmative: 'Sí, ' + getStatusUser.title, btn_negative: 'Cancelar'
    }" />
    <DialogConfirm :showDialog="dialogReuse" :confirm_settings="reuseSettings" @closeDialog="reuseExistingUser" />
  </v-container>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import ExcelJS from "exceljs";
import DialogConfirm from "@/components/DialogConfirm.vue";
import DataIterator from "@/components/DataIterator";

export default {
  data: () => ({
    headers_feature: [
      {
        text: "Permiso",
        align: "start",
        value: "description",
        groupable: false,
      },
      { text: "Pantalla", value: "view_description", align: "right" },
      { text: "Estado", value: "include", align: "center", width: "15%" },
    ],
    codeRule: [(v) => (!!v && /^[0-9]+$/.test(v)) || "Código requerido y válido"],
    passRule: [
      (v) =>
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@$!%*?&])[A-Za-z\d$@$!%*?&]{8,10}[^'\s]/.test(
          v
        ) || "Contraseña no válida",
    ],
    emailRule: [
      (v) =>
        /^\w+([.-_+]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/.test(v) ||
        "Correo no válido",
    ],
    step: 1,
    editStep: 1,
    returnToProfiles: false,
    allGroupsCollapsed: false,
    dialogReuse: false,
    reuseCandidate: null,
    module_filter: 0,
    status_filter: null,
    location_filter: null,
    custom_filter: false,
    module_tab: null,
    dialogConfirm: false,
    dialogChangeStatus: false,
    is_delete: false,
    locationAccess: [],
    constAllLocations: [],
    search: "",
    loading: false,
    exporting: false,
    loadSearch: false,
    users: [],
    statusOptions: [
      { text: "Activos", value: true },
      { text: "Inactivos", value: false },
    ],
    headerUser: [
      { text: "ID", align: "start", value: "use_id", width: "70" },
      { text: "Código", value: "employee_code", width: "100" },
      { text: "Nombre", value: "fullname", width: "200" },
      { text: "Usuario", value: "username", width: "150" },
      {
        text: "Location",
        value: "employee_data[0].location_description_long",
        width: "150",
      },
      { text: "Puesto", value: "employee_data[0].jobtitle_name", width: "150" },
      { text: "Módulos", value: "modules_access", width: "150" },
      { text: "Plantas", value: "locations_quantity", width: "100", align: "center" },
      { text: "Último Acceso", value: "last_login", width: "130" },
      { text: "Estado", value: "status_description", align: "center", width: "120" },
      { text: "Acciones", value: "actions", sortable: false, width: "130" },
    ],
    employee_image: null,
    user: {
      is_edit: false,
      employee_code: null,
      employee_id: null,
      id: null,
      fullname: null,
      username: null,
      email: null,
      use_telephone: null,
      user_business_units: [],
      status: true,
      accaount_default_id: 0,
      profiles: [],
      image_storage: null,
      jobtitle: null,
      department: null,
    },
    itemsBanks: [],
    templates: [],
    system_modules: [],
    system_features: [],
    system_profiles: [],
  }),
  computed: {
    profileImageDetail() {
      return this.user?.image_storage ? this.get_storage(this.user.image_storage) : null;
    },
    selectedModule() {
      if (this.module_tab === null || this.module_tab === undefined) return null;
      return this.system_modules[this.module_tab] || null;
    },
    selectedModuleCounts() {
      if (!this.selectedModule) return { active: 0, total: 0 };
      const feats = this.get_system_features_by_module(this.selectedModule.id);
      return {
        active: feats.filter((f) => f.include).length,
        total: feats.length,
      };
    },
    extraFeaturesCount() {
      if (!this.selectedModule) return 0;
      return this.get_system_features_by_module(this.selectedModule.id).filter(
        (f) => this.isExtraFeature(f)
      ).length;
    },
    reuseSettings() {
      const u = this.reuseCandidate || {};
      return {
        icon: "mdi-account-sync",
        size: "55%",
        title: "Reutilizar cuenta existente",
        sub_title: `Este empleado ya tiene un usuario en Human Connected (usuario @${u.use_username || ""}, correo ${u.use_email || "sin correo"}). ¿Desea reutilizarlo para darle acceso a Finance Connected sin duplicar el usuario?`,
        btn_affirmative: "Sí, reutilizar",
        btn_negative: "No, crear nuevo",
      };
    },
    getStatusUser() {
      let result = {
        title: this.user.status == true ? "activar" : "desactivar",
        status_id: this.user.status == true ? 1 : 2,
        type: this.user.status == true ? "activado" : "desactivado",
      };
      if (this.is_delete == true) {
        result.title = "eliminar";
        result.status_id = 3;
        result.type = "eliminado";
      }
      return result;
    },
    computed_modules() {
      const modules = Object.assign([], this.system_modules);
      modules.unshift({ id: 0, description: "TODOS" });
      return modules;
    },
    users_computed() {
      if (this.module_filter == 0) {
        return this.users;
      }
      return this.users.filter((user) =>
        user.profiless_data.some(
          (profile) => profile.module_id === this.module_filter
        )
      );
    },
    // KPIs Computados
    activeUsersCount() {
      return this.users.filter((user) => user.status).length;
    },
    inactiveUsersCount() {
      return this.users.filter((user) => !user.status).length;
    },
    modulesCount() {
      return this.system_modules.length;
    },
    activeFiltersCount() {
      let count = 0;
      if (this.search) count++;
      if (this.module_filter && this.module_filter !== 0) count++;
      if (this.status_filter !== null) count++;
      if (this.location_filter) count++;
      if (this.custom_filter) count++;
      return count;
    },
    filteredUsers() {
      let filtered = this.users_computed;

      // Filtro por estado
      if (this.status_filter !== null) {
        filtered = filtered.filter((user) => user.status === this.status_filter);
      }

      // Filtro por location
      if (this.location_filter) {
        filtered = filtered.filter((user) =>
          user.location_access.some((loc) => loc.id === this.location_filter)
        );
      }

      // Filtro: solo usuarios con algún acceso custom
      if (this.custom_filter) {
        filtered = filtered.filter((user) =>
          (user.profiless_data || []).some((p) => p.is_custom)
        );
      }

      return filtered;
    },
    totalFeaturesSelected() {
      return this.system_features.filter((f) => f.include).length;
    },
  },
  components: {
    DataIterator,
    DialogConfirm,
  },
  mounted() {
    this.initialized();
  },
  watch: {
    module_tab() {
      // Al cambiar de módulo, los grupos se muestran expandidos por defecto
      this.allGroupsCollapsed = false;
    },
  },
  methods: {
    async initialized() {
      this.loading = true;
      // MODULOS
      const re_system_modules = await this.api_Get("auth/system_modules");
      this.system_modules = await re_system_modules.json();
      for (let item of this.system_modules) {
        Vue.set(this.user, item.module_key, false);
        Vue.set(this.user, item.module_custom_key, false);
      }

      const result = await this.api_Get("humanResource/locations");
      const data = await result.json();

      for (let item of data) {
        let exist = this.constAllLocations.find((element) => element.id == item.id);
        if (exist != undefined) {
          continue;
        }
        this.constAllLocations.push({
          name: item.desc_loc_long + "(" + item.desc_loc_short + ")",
          id: item.id,
          business_id: item.bus_id,
        });
      }
      await this.loadUsers();
      const accounts = await this.api_Post("humanResource/bussinesUnitAccounts", {
        bussines: "123,130",
      });
      const dataAccounts = await accounts.json();
      for (var item of dataAccounts) {
        if (parseInt(item.check_digit) == 2653435) {
          continue;
        }
        this.itemsBanks.push({
          value: item.account_id,
          bank_id: item.bank_id,
          text: item.account_name,
          account: item.check_digit,
          transfer_id: item.transfer_id,
          bussines_unit_id: item.bussines_unit_id,
        });
      }
      // PERFILES QUE PUEDE TENER UN USUARIO
      const re_profiles = await this.api_Get("auth/system_profiles");
      this.system_profiles = await re_profiles.json();

      // FEATURES, CARACTERISTICAS DEL SISTEMA A LOS QUE PUEDE O NO ACCEDER UN USUARIO
      const re_system_features = await this.api_Get("auth/system_features");
      this.system_features = await re_system_features.json();
      // ROLES, LOS FEATURES QUE TIENE ACCESO CADA PERFIL
      const re_templates = await this.api_Get("auth/template_role_access");
      this.templates = await re_templates.json();
      this.loading = false;

      // Si viene ?user_id= (desde Perfiles de Usuarios), abrir ese usuario para editar.
      // Debe ejecutarse al final: editUser necesita system_features y system_profiles ya cargados
      // para poder marcar los accesos (features) y perfiles que el usuario ya tiene.
      const editUserId = this.$route.query.user_id;
      if (editUserId) {
        const u = this.users.find((x) => String(x.use_id) === String(editUserId));
        if (u) {
          this.returnToProfiles = true; // vino desde Perfiles de usuarios
          this.editUser(u, "edit");
        }
      }
    },
    async loadUsers() {
      const re_users = await this.api_Get("auth/users");
      this.users = await re_users.json();

      for (let key in this.users) {
        this.users[key].employee_code = null;
        this.users[key].status_description = this.users[key].status
          ? "Activo"
          : "Inactivo";
        const modules_access = [];
        for (let profile of this.users[key].profiless_data) {
          const module = this.system_modules.find(
            (element) =>
              element.id == profile.module_id || profile.module_id == 0
          );
          if (module == undefined) {
            continue;
          }
          modules_access.push(module.code);
        }
        this.users[key].modules_access = modules_access.join("/");
        this.users[key].locations_quantity =
          this.users[key].location_access.length;
        this.users[key].last_login = this.users[key].last_login
          ? moment(this.users[key].last_login).format("DD/MM/YYYY")
          : "N/A";

        if (this.users[key].employee_data.length > 0) {
          this.users[key].employee_code =
            this.users[key].employee_data[0].employee_code;
        }
      }
    },
    editUser(item, type) {
      this.clearUser();
      if (type == "edit") {
        this.user.is_edit = true;
        if (item.employee_data.length > 0) {
          this.user.employee_code = item.employee_data[0].employee_code;
          this.user.employee_id = item.employee_data[0].employee_id;
          this.user.jobtitle = item.employee_data[0].jobtitle_name;
          this.user.department = item.employee_data[0].department_name;
        }
        this.user.id = item.use_id;
        this.user.status = item.status;
        this.user.fullname = item.fullname;
        this.user.username = item.username;
        this.user.email = item.use_email;
        this.user.use_telephone = item.use_telephone;
        this.user.image_storage = item.image_storage;
      }
      for (let profile of item.profiless_data) {
        this.user[`${profile.module_code}_PERFIL`] = true;
        this.user[`${profile.module_code}_CUSTOM`] = profile.is_custom;
        // Selecciona el primer módulo al que el usuario tiene acceso (por índice real en system_modules)
        if (this.module_tab == null) {
          const idx = this.system_modules.findIndex(
            (m) => m.id === profile.module_id || profile.module_id === 0
          );
          this.module_tab = idx >= 0 ? idx : 0;
        }
        // MARCA LOS PERFILES A LOS QUE TIENE ACCESO EL USUARIO
        this.user.profiles.push({
          id: profile.profile_id,
          module_id: profile.module_id,
          is_custom: profile.is_custom,
        });
        // ACTIVA LAS CARACTERISTICAS QUE TIENE ACCESO
        for (let feature of item.feature_access) {
          const index = this.system_features.findIndex(
            (element) => element.feature_id === feature.system_feature_id
          );
          if (index >= 0) {
            this.system_features[index].include = true;
          }
        }
      }
      this.locationAccess = item.location_access;
      this.editStep = 1;
      this.step = 2;
    },
    async validate_code() {
      // No validar si no hay un código digitado
      if (!this.user.employee_code || String(this.user.employee_code).trim() === "") {
        return;
      }
      try {
        this.loadSearch = true;
        const result = await this.api_Post("humanResource/employees/all", {
          filters:
            " where EMPLOYEE_RECORDS_LAST_ACTION_VIEW_PERSISTENT.employee_code='" +
            this.clearInputFilter(this.user.employee_code, 6) +
            "'",
        });
        const data = await result.json();
        if (result.status == 200) {
          if ((await data.length) == 0) {
            this.$toast.info("No se encontraron resultados!");
            this.loadSearch = false;
            return;
          }
          if (parseInt(data[0].employee_status_id) >= 4) {
            this.$toast.warning("Este empleado no se encuentra activo!");
            this.loadSearch = false;
            return;
          }
          this.user.employee_code = data[0].employee_code;
          this.user.fullname = data[0].employee_fullname;
          this.user.employee_id = data[0].employee_id;
          // Al crear: revisar si el empleado ya tiene cuenta (en FC o en el otro sistema)
          if (this.user.id == null) {
            try {
              const res = await this.api_Get(
                "auth/user_by_employee/" + data[0].employee_id
              );
              const existing = await res.json();
              if (existing && existing.use_id) {
                if (existing.has_fc) {
                  this.$toast.warning(
                    `Este empleado ya tiene un usuario en Finance Connected: ${existing.use_fullname} (@${existing.use_username}).`
                  );
                } else if (existing.has_hr) {
                  // Tiene cuenta en el otro sistema (RRHH): ofrecer reutilizarla
                  this.reuseCandidate = existing;
                  this.dialogReuse = true;
                }
              }
            } catch (e) {
              // silencioso: si falla la verificación no bloquea la creación
            }
          }
        } else {
          this.$toast.error(data.error);
        }
      } catch (error) {
        this.$toast.error("Error: " + error);
      }
      this.loadSearch = false;
    },
    async save() {
      // No permitir guardar sin un empleado vinculado (evita usuarios con Código N/A)
      if (!this.user.employee_id || !this.user.employee_code) {
        this.$toast.warning(
          "Debe seleccionar un empleado válido por su código."
        );
        return;
      }
      if (this.user.id == null) {
        if (
          /^\w+([.-_+]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/.test(
            this.user.email
          ) == false
        ) {
          this.$toast.warning("Correo no válido!");
          return;
        }
      }

      let access = {
        accaount_default_id: this.user.accaount_default_id,
      };
      if (this.user.profiles.length == 0) {
        this.$toast.error("Debe seleccionar al menos un perfil!");
        return;
      }
      let features = [];
      for (let item of this.system_features.filter(
        (element) => element.include == true
      )) {
        features.push(item.feature_id);
      }
      if (features.length == 0) {
        this.$toast.error("Debe seleccionar al menos un acceso!");
        return;
      }
      let userData = {
        user_id: this.user.id,
        employee_id: this.user.employee_id,
        fullname: this.user.fullname,
        username: this.user.username.toLowerCase(),
        email: this.user.email,
        phone: this.user.use_telephone,
        location_ids: this.locationAccess,
        profiles: this.user.profiles,
        permition_id: 2,
        accaount_default_id: this.user.accaount_default_id,
        access_key: JSON.stringify(access),
        created_by: this.$store.state.auth.user.user.user_id,
        features_accecss: features,
      };

      let responseUser;
      if (this.user.id == null) {
        responseUser = await this.api_Post("auth/add_user", userData);
      } else {
        responseUser = await this.api_Put("auth/update_user", userData);
      }
      if (responseUser.status == 200) {
        await this.loadUsers();
        this.$toast.success(
          `Usuario ${this.user.id == null
            ? "registrado con éxito. Se enviará un correo a la dirección especificada."
            : "actualizado con éxito!"
          }`
        );
        this.loadUsers();
        this.exitForm();
        return;
      }
      this.$toast.error(
        "Error al " +
        (this.user.id == null ? "registrar" : "actualizar") +
        " el usuario! " +
        responseUser.error
      );
    },
    async downloadUserXLSX() {
      if (!this.user.id) return;
      this.exporting = true;
      try {
        // Registro completo del usuario (para datos que no están en this.user)
        const record = this.users.find((u) => u.use_id === this.user.id) || {};

        const workbook = new ExcelJS.Workbook();
        const titleFont = { size: 14, bold: true, color: { argb: "FFFFFFFF" } };
        const headerFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1976D2" } };
        const headerFont = { bold: true, color: { argb: "FFFFFFFF" } };
        const applyHeader = (row) => {
          row.eachCell((cell) => {
            cell.fill = headerFill;
            cell.font = headerFont;
            cell.alignment = { vertical: "middle" };
          });
        };

        // ---- Hoja 1: Información del usuario ----
        const infoWs = workbook.addWorksheet("Información");
        infoWs.columns = [{ width: 24 }, { width: 55 }];
        const t = infoWs.addRow(["Acceso del usuario — Finance Connected"]);
        infoWs.mergeCells(`A${t.number}:B${t.number}`);
        t.getCell(1).font = titleFont;
        t.getCell(1).fill = headerFill;
        t.height = 22;
        infoWs.addRow([]);
        const infoRows = [
          ["ID de usuario", this.user.id],
          ["Código de empleado", this.user.employee_code || "N/A"],
          ["Nombre", this.user.fullname || ""],
          ["Usuario", this.user.username || ""],
          ["Correo", this.user.email || ""],
          ["Teléfono", this.user.use_telephone || ""],
          ["Estado", this.user.status ? "Activo" : "Inactivo"],
          ["Puesto", record.employee_data?.[0]?.jobtitle_name || ""],
          ["Último acceso", record.last_login || "N/A"],
          ["Plantas con acceso", this.locationAccess.length],
          ["Perfiles asignados", this.user.profiles.length],
          ["Accesos (features) activos", this.totalFeaturesSelected],
          ["Generado", moment().format("DD/MM/YYYY HH:mm")],
        ];
        for (const [label, value] of infoRows) {
          const r = infoWs.addRow([label, value]);
          r.getCell(1).font = { bold: true };
        }

        // ---- Hoja 2: Perfiles por módulo ----
        const profWs = workbook.addWorksheet("Perfiles");
        profWs.columns = [
          { header: "Módulo", key: "module", width: 32 },
          { header: "Perfil", key: "profile", width: 40 },
          { header: "Tipo", key: "type", width: 16 },
        ];
        applyHeader(profWs.getRow(1));
        for (const p of this.user.profiles) {
          const module = this.system_modules.find((m) => m.id === p.module_id);
          const profile = this.system_profiles.find((sp) => sp.id === p.id);
          profWs.addRow({
            module: module ? module.description : `Módulo ${p.module_id}`,
            profile: profile ? profile.description : `Perfil ${p.id}`,
            type: p.is_custom ? "Personalizado" : "Estándar",
          });
        }

        // ---- Hoja 3: Accesos (features) activos ----
        const featWs = workbook.addWorksheet("Accesos");
        featWs.columns = [
          { header: "Módulo", key: "module", width: 28 },
          { header: "Pantalla", key: "view", width: 38 },
          { header: "Permiso", key: "feature", width: 45 },
          { header: "Código", key: "code", width: 14 },
          { header: "Tipo de acceso", key: "access", width: 18 },
        ];
        applyHeader(featWs.getRow(1));
        const activeFeatures = this.system_features
          .filter((f) => f.include)
          .sort((a, b) =>
            (a.module_description || "").localeCompare(b.module_description || "") ||
            (a.view_description || "").localeCompare(b.view_description || "") ||
            (a.feature_code || "").localeCompare(b.feature_code || "")
          );
        for (const f of activeFeatures) {
          featWs.addRow({
            module: f.module_description || "",
            view: f.view_description || "",
            feature: f.description || "",
            code: f.feature_code || "",
            access: f.access_type || "",
          });
        }
        featWs.autoFilter = { from: "A1", to: "E1" };

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        const safeName = (this.user.username || `usuario_${this.user.id}`).replace(/[^\w.-]+/g, "_");
        link.download = `Accesos_${safeName}_${moment().format("YYYYMMDD")}.xlsx`;
        link.click();
        URL.revokeObjectURL(link.href);
        this.$toast.success("Excel generado con éxito!");
      } catch (error) {
        this.$toast.error("No se pudo generar el Excel: " + error);
      } finally {
        this.exporting = false;
      }
    },
    exitForm() {
      // Si llegó desde "Perfiles de usuarios", regresar allá; si no, volver a la lista
      if (this.returnToProfiles) {
        this.returnToProfiles = false;
        this.$router.push({ name: "UserProfiles" });
        return;
      }
      this.step = 1;
    },
    clearUser() {
      this.user.is_edit = false;
      this.user.employee_code = null;
      this.user.id = null;
      this.user.fullname = null;
      this.user.username = null;
      this.user.email = null;
      this.user.use_telephone = null;
      this.user.accaount_default_id = 0;
      this.user.status = true;
      this.user.profiles = [];
      this.module_tab = null;

      for (let item of this.system_modules) {
        this.user[item.module_key] = false;
        this.user[item.module_custom_key] = false;
      }
      for (let key in this.system_features) {
        this.system_features[key].include = false;
      }
      this.locationAccess = [];
      this.user.image_storage = null;
      this.user.jobtitle = null;
      this.user.department = null;
      this.employee_image = null;
    },
    async resetPassword(res) {
      if (res == false) {
        this.dialogConfirm = false;
        return;
      }
      if (
        /^([a-zA-Z0-9_\.\-])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/.test(
          this.user.email
        ) == false
      ) {
        this.$toast.error("Este usuario no cuenta con un correo válido!");
        return;
      }
      const response = await this.api_Post("auth/resetPassword", {
        id: this.user.id,
        username: this.user.username,
        name: this.user.fullname,
        email: this.user.email,
        created_by: this.$store.state.auth.user.user.user_id,
      });
      if (response.status == 200) {
        this.$toast.success(
          "Se ha enviado la nueva contraseña a " + this.user.email
        );
      } else {
        this.$toast.error("Error! " + response.error);
      }
      this.dialogConfirm = false;
    },
    async changeUserStatus(res) {
      if (res == false) {
        this.dialogChangeStatus = false;
        this.user.status = !this.user.status;
        return;
      }
      if (this.user.id == null) {
        this.dialogChangeStatus = false;
        this.user.status = true;
        return;
      }
      console.log(this.getStatusUser)
      const responseUser = await this.api_Put("auth/change_status", {
        user_id: this.user.id,
        status_id: this.getStatusUser.status_id == 2 ? false : true,
        system_id: 2,
        created_by: this.$store.state.auth.user.user.user_id,
      });
      if (responseUser.status == 200) {
        this.$toast.success(
          "Usuario " + this.getStatusUser.type + " con éxito"
        );
        await this.loadUsers();
        this.step = 1;
        this.dialogChangeStatus = false;
        return;
      }
      this.$toast.error("Error! " + responseUser.error);
    },
    async validateAvailability() {
      if (this.user.username == null) {
        return;
      }
      if (this.user.username.length < 5) {
        this.$toast.error("Nombre de usuario no válido!");
        this.user.username = null;
        return;
      }
      const result = await this.api_Get(
        "auth/validate_availability_username/" + this.user.username
      );
      if (result.status == 200) {
        const data = await result.json();
        if (data.length > 0) {
          this.$toast.error("Este nombre de usuario no está disponible!");
          this.user.username = null;
          return;
        }
      }
    },
    reuseExistingUser(accept) {
      if (accept && this.reuseCandidate) {
        // Reutiliza la cuenta existente: al guardar hará update_user (adjunta acceso FC sin duplicar)
        this.user.id = this.reuseCandidate.use_id;
        this.user.is_edit = true;
        this.user.username = this.reuseCandidate.use_username;
        this.user.email = this.reuseCandidate.use_email;
        this.user.use_telephone = this.reuseCandidate.use_telephone;
        if (this.reuseCandidate.use_fullname) {
          this.user.fullname = this.reuseCandidate.use_fullname;
        }
        this.$toast.info(
          "Se reutilizará la cuenta existente. Asigna los accesos de Finance Connected y guarda."
        );
      }
      this.dialogReuse = false;
      this.reuseCandidate = null;
    },
    toggleAllGroups() {
      const t = this.$refs.permTable;
      if (!t || !t.openCache) return;
      this.allGroupsCollapsed = !this.allGroupsCollapsed;
      const open = !this.allGroupsCollapsed; // true = abierto
      // Asegura que todos los grupos del módulo estén en el cache y aplica el estado
      const groups = [
        ...new Set(
          this.get_system_features_by_module(this.selectedModule.id).map(
            (f) => f.view_description
          )
        ),
      ];
      groups.forEach((g) => this.$set(t.openCache, g, open));
      // Por si quedaron grupos de un módulo anterior en el cache
      Object.keys(t.openCache).forEach((k) => this.$set(t.openCache, k, open));
    },
    get_system_features_by_module(module) {
      if (module == 0) {
        return this.system_features;
      }
      return this.system_features.filter((element) => element.module_id == module);
    },
    isExtraFeature(item) {
      // Feature marcado (incluido) que NO pertenece al template del perfil del módulo.
      // Es un acceso "extra" asignado de forma custom al usuario.
      if (!item || !item.include) return false;
      const profile = this.user.profiles.find(
        (p) => p.module_id === item.module_id
      );
      if (!profile) return true; // incluido sin perfil en el módulo => extra
      const inTemplate = this.templates.some(
        (t) =>
          t.profile_id === profile.id &&
          t.system_feature_id === item.feature_id
      );
      return !inTemplate;
    },
    get_color(item) {
      const is_selected = this.user.profiles.find(
        (element) => element.id == item.id
      );
      return is_selected == undefined ? "primary" : "success";
    },
    add_user_profile(profile, module) {
      this.user.profiles = this.user.profiles.filter(
        (element) => element.module_id != profile.module_id
      );
      // Aplicar un perfil resetea el acceso al template => deja de ser custom
      profile.is_custom = false;
      this.user.profiles.push(profile);
      const roles = this.templates.filter(
        (element) => element.profile_id == profile.id
      );

      let features_includes = this.get_system_features_by_module(
        profile.module_id
      );

      for (let key in features_includes) {
        const is_include = roles.find(
          (element) =>
            element.system_feature_id == features_includes[key].feature_id
        );
        if (is_include != null) {
          features_includes[key].include = true;
          continue;
        }
        features_includes[key].include = false;
      }
      this.user[module.module_custom_key] = false;
    },
    update_custom_etting(module) {
      this.$nextTick();
      let index = this.user.profiles.findIndex(
        (element) => element.module_id === module.id
      );
      if (index < 0) {
        return;
      }
      this.user.profiles[index].is_custom =
        this.user[module.module_custom_key];

      const roles = this.templates.filter(
        (element) => element.profile_id == this.user.profiles[index].id
      );
      let features_includes = this.get_system_features_by_module(module.id);

      for (let key in features_includes) {
        const is_include = roles.find(
          (element) =>
            element.system_feature_id == features_includes[key].feature_id
        );
        if (is_include != null) {
          features_includes[key].include = true;
          continue;
        }
        features_includes[key].include = false;
      }
    },
    add_or_remove_module_user(module) {
      this.$nextTick();
      this.user[module.module_custom_key] = false;
      if (this.user[module.module_key] == false) {
        let index = this.user.profiles.findIndex(
          (element) => element.module_id === module.id
        );
        this.user.profiles.splice(index, 1);
      }
      let features_includes = this.get_system_features_by_module(module.id);
      for (let key in features_includes) {
        features_includes[key].include = false;
      }
    },
    check_cant_edit(module) {
      if (this.user[module.module_custom_key] == false) {
        return true;
      }
      const profile = this.user.profiles.find(
        (element) => element.module_id === module.id
      );
      if (profile == undefined) {
        return true;
      }
      return false;
    },
    // Métodos auxiliares
    getInitials(name) {
      if (!name) return "NN";
      const names = name.split(" ");
      if (names.length >= 2) {
        return (names[0][0] + names[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    getModuleColor(moduleCode) {
      const colors = {
        REC: "blue",
        NOM: "green",
        CAP: "orange",
        SEG: "red",
        PER: "purple",
        REP: "teal",
      };
      return colors[moduleCode] || "grey";
    },
    isRecentLogin(lastLogin) {
      if (lastLogin === "N/A") return false;
      const loginDate = moment(lastLogin, "DD/MM/YYYY");
      const daysDiff = moment().diff(loginDate, "days");
      return daysDiff <= 7;
    },
    getUsersByModule(moduleId) {
      return this.users.filter((user) =>
        user.profiless_data.some((profile) => profile.module_id === moduleId)
      ).length;
    },
    clearAllFilters() {
      this.search = "";
      this.module_filter = 0;
      this.status_filter = null;
      this.location_filter = null;
      this.custom_filter = false;
    },
    viewUserDetails(item) {
      this.editUser(item, "edit");
    },
  },
};
</script>

<style scoped>
/* Estilos mejorados */
.gradient-primary {
  background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-primary-lighten1) 100%) !important;
}

/* Header + filtros fijos al hacer scroll en la lista de usuarios */
.user-filters-sticky {
  position: sticky;
  top: 0;
  z-index: 6;
  background: #fff;
}

.gradient-success {
  background: linear-gradient(135deg, var(--v-success-base) 0%, var(--v-success-lighten1) 100%) !important;
}

.gradient-warning {
  background: linear-gradient(135deg, var(--v-warning-base) 0%, var(--v-warning-lighten1) 100%) !important;
}

.gradient-info {
  background: linear-gradient(135deg, var(--v-info-base) 0%, var(--v-info-lighten1) 100%) !important;
}

.glass-header {
  background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-primary-darken1) 100%) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.glass-card {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.opacity-70 {
  opacity: 0.7;
}

.opacity-80 {
  opacity: 0.8;
}

.rounded-xl {
  border-radius: 20px !important;
}

.fill-height {
  min-height: 82vh;
}

.left-column {
  position: sticky;
  top: 0;
  height: 82vh;
  overflow-y: auto;
  background-color: #fafafa;
}

.scroll-column {
  height: 82vh;
  overflow-y: auto;
}

.scrollable-content {
  padding: 1px;
}

.fixed-content {
  position: sticky;
  top: 0;
}

/* Tabla con filas alternas */
.striped-table>>>tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.02);
}

.striped-table>>>tbody tr:hover {
  background-color: rgba(33, 150, 243, 0.08) !important;
}

/* Animaciones sutiles */
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  transform: translateY(-2px);
}

/* Mejorar legibilidad */
.v-data-table>>>th {
  font-weight: 600 !important;
  color: rgba(0, 0, 0, 0.87) !important;
  background-color: #f5f5f5 !important;
}

/* Chips más atractivos */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.5px;
}

/* Scrollbar personalizado */
.scroll-column::-webkit-scrollbar,
.left-column::-webkit-scrollbar {
  width: 8px;
}

.scroll-column::-webkit-scrollbar-track,
.left-column::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.scroll-column::-webkit-scrollbar-thumb,
.left-column::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.scroll-column::-webkit-scrollbar-thumb:hover,
.left-column::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Opacidad para iconos */
.opacity-50 {
  opacity: 0.5;
}

/* Headers mejorados */
.v-card-title {
  font-family: "Roboto", sans-serif;
  letter-spacing: 0.5px;
}

/* Badges y chips responsivos */
@media (max-width: 960px) {
  .left-column {
    height: auto;
    position: relative;
  }

  .scroll-column {
    height: auto;
  }
}
</style>